"use strict";
/**
 * Swiss Ephemeris Type Definitions - Implementation Classes
 *
 * This file contains implementation classes that add convenience methods
 * to the result interfaces, making the library more developer-friendly.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateTimeImpl = exports.SolarEclipseImpl = exports.LunarEclipseImpl = void 0;
const enums_js_1 = require("./enums.js");
/**
 * Implementation of LunarEclipse with convenience methods
 */
class LunarEclipseImpl {
    constructor(type, maximum, partialBegin, partialEnd, totalBegin, totalEnd, penumbralBegin, penumbralEnd) {
        this.type = type;
        this.maximum = maximum;
        this.partialBegin = partialBegin;
        this.partialEnd = partialEnd;
        this.totalBegin = totalBegin;
        this.totalEnd = totalEnd;
        this.penumbralBegin = penumbralBegin;
        this.penumbralEnd = penumbralEnd;
    }
    isTotal() {
        return (this.type & enums_js_1.EclipseType.Total) !== 0;
    }
    isPartial() {
        return (this.type & enums_js_1.EclipseType.Partial) !== 0;
    }
    isPenumbralOnly() {
        return ((this.type & enums_js_1.EclipseType.Penumbral) !== 0 &&
            (this.type & (enums_js_1.EclipseType.Total | enums_js_1.EclipseType.Partial)) === 0);
    }
    getTotalityDuration() {
        if (!this.isTotal() || this.totalBegin === 0 || this.totalEnd === 0) {
            return 0;
        }
        // Convert from Julian days to hours
        const duration = (this.totalEnd - this.totalBegin) * 24;
        return duration > 0 ? duration : 0;
    }
    getPartialDuration() {
        if (this.partialBegin === 0 || this.partialEnd === 0) {
            return 0;
        }
        // Convert from Julian days to hours
        const duration = (this.partialEnd - this.partialBegin) * 24;
        return duration > 0 ? duration : 0;
    }
    getTotalDuration() {
        if (this.penumbralBegin === 0 || this.penumbralEnd === 0) {
            return 0;
        }
        // Convert from Julian days to hours
        const duration = (this.penumbralEnd - this.penumbralBegin) * 24;
        return duration > 0 ? duration : 0;
    }
}
exports.LunarEclipseImpl = LunarEclipseImpl;
/**
 * Implementation of SolarEclipse with convenience methods
 */
class SolarEclipseImpl {
    constructor(type, maximum, partialBegin, partialEnd, centralBegin, centralEnd, centerLineBegin, centerLineEnd) {
        this.type = type;
        this.maximum = maximum;
        this.partialBegin = partialBegin;
        this.partialEnd = partialEnd;
        this.centralBegin = centralBegin;
        this.centralEnd = centralEnd;
        this.centerLineBegin = centerLineBegin;
        this.centerLineEnd = centerLineEnd;
    }
    isTotal() {
        return (this.type & enums_js_1.EclipseType.Total) !== 0;
    }
    isAnnular() {
        return (this.type & enums_js_1.EclipseType.Annular) !== 0;
    }
    isHybrid() {
        return (this.type & enums_js_1.EclipseType.AnnularTotal) !== 0;
    }
    isPartial() {
        return (this.type & enums_js_1.EclipseType.Partial) !== 0;
    }
    isCentral() {
        return (this.type & enums_js_1.EclipseType.Central) !== 0;
    }
    isNonCentral() {
        return (this.type & enums_js_1.EclipseType.NonCentral) !== 0;
    }
}
exports.SolarEclipseImpl = SolarEclipseImpl;
/**
 * Implementation of ExtendedDateTime with convenience methods
 */
class DateTimeImpl {
    constructor(year, month, day, hour, calendarType = enums_js_1.CalendarType.Gregorian) {
        this.year = year;
        this.month = month;
        this.day = day;
        this.hour = hour;
        this.calendarType = calendarType;
    }
    toISOString() {
        const hours = Math.floor(this.hour);
        const minutes = Math.floor((this.hour - hours) * 60);
        const seconds = Math.floor(((this.hour - hours) * 60 - minutes) * 60);
        const milliseconds = Math.floor((((this.hour - hours) * 60 - minutes) * 60 - seconds) * 1000);
        const yearStr = Math.abs(this.year).toString().padStart(4, '0');
        const yearSign = this.year < 0 ? '-' : '';
        const monthStr = this.month.toString().padStart(2, '0');
        const dayStr = this.day.toString().padStart(2, '0');
        const hoursStr = hours.toString().padStart(2, '0');
        const minutesStr = minutes.toString().padStart(2, '0');
        const secondsStr = seconds.toString().padStart(2, '0');
        const msStr = milliseconds.toString().padStart(3, '0');
        return `${yearSign}${yearStr}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}:${secondsStr}.${msStr}Z`;
    }
    toString() {
        const calType = this.calendarType === enums_js_1.CalendarType.Gregorian ? 'Gregorian' : 'Julian';
        const yearStr = this.year < 0 ? `${Math.abs(this.year)} BCE` : this.year.toString();
        return `${yearStr}-${this.month.toString().padStart(2, '0')}-${this.day.toString().padStart(2, '0')} ${this.hour.toFixed(6)} hours (${calType})`;
    }
}
exports.DateTimeImpl = DateTimeImpl;
